// Simple mode (§11.19): one column of large buttons, steps 1–5 — open, cleaners, filters, remove
// background, save — plus big Undo / Start over / Hold to see the original. Everything edits the same
// Settings as Pro mode, so switching never loses work.
import { useState, type ReactNode } from 'react';
import { useT, type MessageKey } from '../../i18n';
import { useLibrary } from '../../state/library';
import { useEditor } from '../../state/editor';
import { useUi } from '../../state/ui';
import { useSimple } from '../../state/simple';
import { pickFilesFallback } from '../../io/fs';
import { openPhotos } from '../../io/open';
import { openFolder } from '../../state/persist';
import { FallbackFolder } from '../../io/fs';
import {
  CLEANERS,
  CROP_SHAPES,
  cleanerOn,
  cropFor,
  startOver,
  toggleCleaner,
} from '../../features/simpleCleaners';
import { simpleSave, type SaveTarget } from '../../features/simpleActions';
import { useMatte } from '../../features/background/matte';
import { FilterGrid } from './FilterGrid';
import { MakeBigger } from './MakeBigger';
import { makeAdFromPhotos } from '../../design/ad/actions';
import { AiPhotoButtons } from '../dialogs/AiDialogs';
import { useWorkspace } from '../../state/workspace';
import { errorKey } from '../../i18n/errors';
import type { Rgb } from '../../model/types';

const BIG = { minHeight: 48, fontSize: 15, padding: '8px 10px', width: '100%' } as const;
const SAVES: { id: SaveTarget; label: MessageKey }[] = [
  { id: 'instagram', label: 'saveInstagram' },
  { id: 'whatsapp', label: 'saveWhatsapp' },
  { id: 'menu', label: 'saveMenu' },
  { id: 'delivery', label: 'saveDelivery' },
  { id: 'png', label: 'savePng' },
  { id: 'original', label: 'saveOriginal' },
];
const hex = (c: Rgb) =>
  '#' +
  c
    .map((v) =>
      Math.round(Math.min(1, Math.max(0, v)) * 255)
        .toString(16)
        .padStart(2, '0'),
    )
    .join('');

function Step({ n, title, children }: { n: number; title: MessageKey; children: ReactNode }) {
  const t = useT();
  return (
    <section style={{ padding: '10px 12px', borderBottom: '1px solid var(--line)' }}>
      <h3 style={{ margin: '0 0 8px', fontSize: 15, display: 'flex', gap: 8, alignItems: 'center' }}>
        <span
          aria-hidden="true"
          style={{
            width: 22,
            height: 22,
            borderRadius: 11,
            background: 'var(--accent)',
            color: '#fff',
            display: 'inline-grid',
            placeItems: 'center',
            fontSize: 13,
          }}
        >
          {n}
        </span>
        {t(title)}
      </h3>
      {children}
    </section>
  );
}

export async function openPhotoFiles() {
  const files = await pickFilesFallback();
  if (files.length) await openFolder(new FallbackFolder(files));
}

export function SimplePanel() {
  const t = useT();
  const id = useLibrary((s) => s.activeId);
  const photoCount = useLibrary((s) => s.photos.length);
  const settings = useEditor((s) => (id ? s.settings[id] : undefined)) ?? useEditor.getState().get('');
  const undoCount = useEditor((s) => (id ? (s.history[id]?.past.length ?? 0) : 0));
  const tool = useUi((s) => s.tool);
  const message = useSimple((s) => s.message);
  useSimple((s) => s.cleaners); // re-render when a cleaner record changes
  const bgState = useMatte((m) => (id ? m.status[id]?.state : undefined));
  const [cropOpen, setCropOpen] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [saveMsg, setSaveMsg] = useState<string | null>(null);
  const has = !!id;

  const tap = async (cid: (typeof CLEANERS)[number]['id']) => {
    if (cid === 'cropFor') {
      setCropOpen(!cropOpen);
      return;
    }
    setBusy(cid);
    useSimple.getState().set({ message: null });
    try {
      await toggleCleaner(cid);
    } finally {
      setBusy(null);
    }
  };
  const save = async (target: SaveTarget) => {
    setBusy(`save-${target}`);
    setSaveMsg(null);
    try {
      setSaveMsg(t('saved', { name: await simpleSave(target) }));
    } catch (e) {
      console.warn('save failed', e);
      setSaveMsg(t('saveFailed', { reason: t(errorKey(e)) }));
    } finally {
      setBusy(null);
    }
  };
  const bg = settings.background;

  return (
    <div data-testid="simple-panel" style={{ display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', gap: 6, padding: '10px 12px', borderBottom: '1px solid var(--line)' }}>
        <button
          style={{ ...BIG, flex: 1 }}
          data-testid="simple-undo"
          disabled={!undoCount}
          onClick={() => id && useEditor.getState().undo(id)}
        >
          ↶ {t('undoBig')}
        </button>
        <button
          style={{ ...BIG, flex: 1 }}
          data-testid="simple-start-over"
          disabled={!has}
          onClick={startOver}
        >
          {t('startOver')}
        </button>
      </div>
      <div style={{ padding: '0 12px 10px', borderBottom: '1px solid var(--line)' }}>
        <button
          style={BIG}
          data-testid="simple-hold-original"
          disabled={!has}
          onPointerDown={() => useUi.getState().set({ before: true })}
          onPointerUp={() => useUi.getState().set({ before: false })}
          onPointerLeave={() => useUi.getState().set({ before: false })}
        >
          👁 {t('holdOriginal')}
        </button>
      </div>

      <Step n={1} title="stepOpen">
        <button
          style={{ ...BIG, background: 'var(--accent)', color: '#fff' }}
          data-testid="simple-open"
          onClick={() => void openPhotoFiles()}
        >
          📂 {t('simpleOpen')}
        </button>
        <button
          style={{
            marginTop: 6,
            background: 'none',
            border: 'none',
            color: 'var(--muted)',
            textDecoration: 'underline',
          }}
          onClick={() => void openPhotos()}
        >
          {t('simpleOpenFolder')}
        </button>
        {photoCount > 1 && (
          <div style={{ fontSize: 12, color: 'var(--muted)' }}>{t('photoCount', { n: photoCount })}</div>
        )}
      </Step>

      <Step n={2} title="stepClean">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
          {CLEANERS.filter((c) => c.id !== 'removeBackground').map((c) => {
            const on =
              has &&
              (c.id === 'cropFor' ? cleanerOn(id!, 'cropFor', settings) : cleanerOn(id!, c.id, settings));
            return (
              <button
                key={c.id}
                data-testid={`cleaner-${c.id}`}
                aria-pressed={on}
                disabled={!has || (!!busy && busy !== c.id)}
                onClick={() => void tap(c.id)}
                style={{
                  minHeight: 48,
                  textAlign: 'left',
                  ...(on ? { background: 'var(--accent)', color: '#fff' } : {}),
                }}
              >
                {on ? '✓ ' : ''}
                {t(c.label)}
                {busy === c.id ? ' …' : ''}
              </button>
            );
          })}
        </div>
        {cropOpen && (
          <div
            style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginTop: 6 }}
            data-testid="crop-shapes"
          >
            {CROP_SHAPES.map((c) => (
              <button
                key={c.aspect}
                style={{ minHeight: 40 }}
                onClick={() => cropFor(c.aspect)}
                data-testid={`crop-${c.aspect}`}
              >
                {t(c.label)}
              </button>
            ))}
          </div>
        )}
        <div style={{ marginTop: 8 }}>
          <MakeBigger />
        </div>
        <AiPhotoButtons />
        {tool === 'spot' && <div style={{ fontSize: 12, marginTop: 6 }}>{t('clSpotsHint')}</div>}
        {tool === 'cropPan' && <div style={{ fontSize: 12, marginTop: 6 }}>{t('clCropHint')}</div>}
        {message && (
          <div
            role="status"
            data-testid="simple-message"
            style={{ fontSize: 13, marginTop: 6, color: '#ffb347' }}
          >
            {t(message as MessageKey)}
          </div>
        )}
      </Step>

      <Step n={3} title="stepFilter">
        {has ? <FilterGrid /> : null}
      </Step>

      <Step n={4} title="stepBackground">
        <button
          style={BIG}
          data-testid="simple-bg"
          aria-pressed={bg.mode !== 'off'}
          disabled={!has || busy === 'bg'}
          onClick={async () => {
            setBusy('bg');
            try {
              await toggleCleaner('removeBackground');
            } catch {
              /* the status line below says why */
            } finally {
              setBusy(null);
            }
          }}
        >
          {bg.mode !== 'off' ? '✓ ' : ''}✂ {t('clRemoveBackground')}
          {bgState === 'loading' || bgState === 'running' ? ' …' : ''}
        </button>
        {bgState === 'error' && (
          <div style={{ fontSize: 12, color: '#ff6b6b', marginTop: 4 }}>{t('bgFailedSimple')}</div>
        )}
        {bg.mode !== 'off' && id && (
          <div role="radiogroup" style={{ display: 'flex', gap: 6, marginTop: 6, alignItems: 'center' }}>
            <button
              role="radio"
              aria-checked={bg.fill.kind === 'transparent'}
              data-testid="simple-bg-transparent"
              onClick={() =>
                useEditor
                  .getState()
                  .update(id, (s) => void (s.background.fill = { ...s.background.fill, kind: 'transparent' }))
              }
              style={{ minHeight: 40, flex: 1 }}
            >
              {t('bgTransparent')}
            </button>
            <button
              role="radio"
              aria-checked={bg.fill.kind === 'color' && hex(bg.fill.color) === '#ffffff'}
              data-testid="simple-bg-white"
              onClick={() =>
                useEditor
                  .getState()
                  .update(id, (s) => void (s.background.fill = { kind: 'color', color: [1, 1, 1] }))
              }
              style={{ minHeight: 40, flex: 1 }}
            >
              {t('bgWhite')}
            </button>
            <input
              type="color"
              aria-label={t('bgColor')}
              value={hex(bg.fill.color)}
              onChange={(e) =>
                useEditor.getState().update(id, (s) => {
                  const h = e.target.value;
                  s.background.fill = {
                    kind: 'color',
                    color: [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16) / 255) as Rgb,
                  };
                })
              }
              style={{ width: 48, height: 40 }}
            />
          </div>
        )}
      </Step>

      <Step n={5} title="stepSave">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
          {SAVES.map((s) => (
            <button
              key={s.id}
              data-testid={`save-${s.id}`}
              disabled={!has || !!busy}
              onClick={() => void save(s.id)}
              style={{ minHeight: 48 }}
            >
              💾 {t(s.label)}
              {busy === `save-${s.id}` ? ' …' : ''}
            </button>
          ))}
        </div>
        {saveMsg && (
          <div role="status" data-testid="simple-save-status" style={{ fontSize: 12, marginTop: 6 }}>
            {saveMsg}
          </div>
        )}
        <button
          style={{ ...BIG, marginTop: 10, background: 'var(--accent)', color: '#fff' }}
          data-testid="make-ad"
          disabled={!!busy}
          title={t('makeAdHint')}
          onClick={makeAdFromPhotos}
        >
          🪧 {t('makeAd')}
        </button>
        <button
          style={{ ...BIG, marginTop: 6 }}
          data-testid="plating-guide"
          onClick={() => useWorkspace.getState().setWs('plating')}
        >
          🍽 {t('platingGuide')}
        </button>
      </Step>
    </div>
  );
}
